import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { AppDispatch, RootState } from '../../../stateStore'
import employeeHandbookSettingsApi from '../../../middleware/api/EmployeeHandbook/HandbookSettings/employeeHandbookSettingsApi'
import {
  AddNewHandbookPage,
  EditPolicyMessageType,
  EmployeeCountry,
  EmployeeHandbook,
  EmployeeHandbookListApiProps,
  EmployeeHandbookPolicyReportApiProps,
  EmployeeHandbookSettingSliceState,
  exportHandbookPolicyReportApiProps,
  HandbookPolicyList,
  TotalHandbookList,
  UpdateHandbookPage,
} from '../../../types/EmployeeHandbook/HandbookSettings/employeeHandbookSettingsTypes'

const getEmployeeHandbooks = createAsyncThunk(
  'employeeHandbookSettings/getEmployeeHandbooks',
  async (props: EmployeeHandbookListApiProps, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.getEmployeeHandbooks(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getHandbookPolicyReport = createAsyncThunk(
  'employeeHandbookSettings/getHandbookPolicyReport',
  async (props: EmployeeHandbookPolicyReportApiProps, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.getHandbookPolicyReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const exportHandbookPolicyReport = createAsyncThunk(
  'employeeHandbookSettings/exportHandbookPolicyReport',
  async (props: exportHandbookPolicyReportApiProps, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.exportHandbookPolicyReport(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getTotalHandbookList = createAsyncThunk<
  TotalHandbookList[] | undefined,
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('employeeHandbookSettings/getTotalHandbookList', async (_, thunkApi) => {
  try {
    return await employeeHandbookSettingsApi.getTotalHandbookList()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const deleteEmployeeHandbook = createAsyncThunk<
  number | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'employeeHandbookSettings/deleteEmployeeHandbook',
  async (bookId, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.deleteEmployeeHandbook(bookId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getEmployeeCountries = createAsyncThunk<
  EmployeeCountry[] | undefined,
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('employeeHandbookSettings/getEmployeeCountries', async (_, thunkApi) => {
  try {
    return await employeeHandbookSettingsApi.getEmployeeCountries()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const addNewHandbook = createAsyncThunk<
  number | undefined,
  AddNewHandbookPage,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'employeeHandbookSettings/addNewHandbook',
  async (addEmployeeHandbook: AddNewHandbookPage, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.addNewHandbook(
        addEmployeeHandbook,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addHandBookDiscription = createAsyncThunk<
  void,
  EditPolicyMessageType,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'employeeHandbookSettings/addHandBookDiscription',
  async (editPolicyMessageType: EditPolicyMessageType, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.addHandBookDiscription(
        editPolicyMessageType,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getMessageDescription = createAsyncThunk<
  EditPolicyMessageType[] | undefined, // Adjusted the return type to match the API call
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('employeeHandbookSettings/getMessageDescription', async (_, thunkApi) => {
  try {
    return await employeeHandbookSettingsApi.getMessageDescription()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getEmployeeHandbook = createAsyncThunk<
  EmployeeCountry[] | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'employeeHandbookSettings/getEmployeeHandbook',
  async (handbookId: number, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.getSelectedCountries(handbookId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateEmployeeHandbook = createAsyncThunk<
  number | undefined,
  UpdateHandbookPage,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'employeeHandbookSettings/updateEmployeeHandbook',
  async (updateHandbook: UpdateHandbookPage, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.updateEmployeeHandbook(
        updateHandbook,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllPublishedPolicies = createAsyncThunk(
  'employeeHandbookSettings/getAllPublishedPolicies',
  async (_, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.getAllPublishedPolicies()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getPolicyVersion = createAsyncThunk(
  'employeeHandbookSettings/getPolicyVersion',
  async (_, thunkApi) => {
    try {
      return await employeeHandbookSettingsApi.getPolicyVersion()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialEmployeeHandbookSettingState: EmployeeHandbookSettingSliceState = {
  listSize: 0,
  isLoading: ApiLoadingState.idle,
  isLoadingGet: ApiLoadingState.idle,
  employeeHandbooks: [],
  employeeCountries: [],
  error: null,
  totalHandbookList: [],
  updateHandbookPage: {} as UpdateHandbookPage,
  selectedHandbook: [],
  selectedCountries: [],
  getAllPoliciesDashboard: [],
  isPublishPolicy: false,
  editorDescription: '',
  editHandbook: {} as UpdateHandbookPage,
  versionLength: 0,
  editPolicyMessage: {
    id: -1,
    discription: '',
    discription2: '',
  } as EditPolicyMessageType,
  handbookPolicyReport: [],
}

const employeeHandbookSettingSlice = createSlice({
  name: 'employeeHandbookSettings',
  initialState: initialEmployeeHandbookSettingState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setIsUpdate: (state, action) => {
      state.isPublishPolicy = action.payload
    },
    setEditorDescription: (state, action) => {
      state.editorDescription = action.payload
    },
    setEditHandbook: (state, action) => {
      state.editHandbook = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTotalHandbookList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.totalHandbookList = action.payload as TotalHandbookList[]
      })
      .addCase(getPolicyVersion.fulfilled, (state, action) => {
        if (action.payload.length === 0) {
          state.versionLength = 0
        } else {
          const highestVersionItem = action.payload.reduce(
            (max, item) => (item.version > max.version ? item : max),
            action.payload[0],
          )
          state.versionLength = highestVersionItem.version
        }
      })
      .addCase(getEmployeeHandbooks.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employeeHandbooks = action.payload.list
        state.listSize = action.payload.size
        state.isLoadingGet = ApiLoadingState.succeeded
      })
      .addCase(getHandbookPolicyReport.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.handbookPolicyReport = action.payload.list
        state.listSize = action.payload.size
        state.isLoadingGet = ApiLoadingState.succeeded
      })
      .addCase(getHandbookPolicyReport.pending, (state) => {
        state.isLoadingGet = ApiLoadingState.loading
      })
      .addCase(getEmployeeHandbooks.pending, (state) => {
        state.isLoadingGet = ApiLoadingState.loading
      })
      .addCase(getEmployeeHandbook.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.selectedCountries = action.payload as EmployeeCountry[]
      })
      .addCase(getAllPublishedPolicies.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllPoliciesDashboard = action.payload as EmployeeHandbook[]
      })
      .addCase(getMessageDescription.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        if (action.payload && action.payload.length > 0) {
          state.editPolicyMessage = action.payload[0] as EditPolicyMessageType
        }
      })
      .addMatcher(isAnyOf(getEmployeeCountries.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.employeeCountries = action.payload as EmployeeCountry[]
      })

      .addMatcher(
        isAnyOf(
          addNewHandbook.fulfilled,
          addHandBookDiscription.fulfilled,
          getTotalHandbookList.fulfilled,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.succeeded
        },
      )
      .addMatcher(
        isAnyOf(
          getEmployeeHandbooks.pending,
          getTotalHandbookList.pending,
          deleteEmployeeHandbook.pending,
          getEmployeeCountries.pending,
          addNewHandbook.pending,
          getEmployeeHandbook.pending,
          addHandBookDiscription.pending,
          getMessageDescription.pending,
          getHandbookPolicyReport.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getEmployeeHandbooks.rejected,
          getTotalHandbookList.rejected,
          getEmployeeCountries.rejected,
          deleteEmployeeHandbook.rejected,
          addNewHandbook.rejected,
          getEmployeeHandbook.rejected,
          addHandBookDiscription.rejected,
          getMessageDescription.rejected,
          getHandbookPolicyReport.rejected,
        ),
        (state, action) => {
          state.isLoading = ApiLoadingState.failed
          state.error = action.payload as ValidationError
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.employeeHandbookSettings.isLoading

const getIsLoadingGet = (state: RootState): LoadingState =>
  state.employeeHandbookSettings.isLoadingGet

const employeeHandbooks = (state: RootState): EmployeeHandbook[] =>
  state.employeeHandbookSettings.employeeHandbooks

const listSize = (state: RootState): number =>
  state.employeeHandbookSettings.listSize

const employeeCountries = (state: RootState): EmployeeCountry[] =>
  state.employeeHandbookSettings.employeeCountries

const selectedCountries = (state: RootState): EmployeeCountry[] =>
  state.employeeHandbookSettings.selectedCountries

const totalHandbookList = (state: RootState): TotalHandbookList[] =>
  state.employeeHandbookSettings.totalHandbookList

const updateHandbookPage = (state: RootState): UpdateHandbookPage =>
  state.employeeHandbookSettings.updateHandbookPage

const getAllPoliciesDashboard = (state: RootState): EmployeeHandbook[] =>
  state.employeeHandbookSettings.getAllPoliciesDashboard

const getDescription = (state: RootState): string =>
  state.employeeHandbookSettings.editorDescription

const getEditHandbook = (state: RootState): UpdateHandbookPage =>
  state.employeeHandbookSettings.editHandbook

const getVersionLength = (state: RootState): number =>
  state.employeeHandbookSettings.versionLength

const getEditPolicyMessage = (state: RootState): EditPolicyMessageType =>
  state.employeeHandbookSettings.editPolicyMessage

const getHandbookPolicyList = (state: RootState): HandbookPolicyList[] =>
  state.employeeHandbookSettings.handbookPolicyReport

export const employeeHandbookSettingsThunk = {
  getEmployeeHandbooks,
  getTotalHandbookList,
  deleteEmployeeHandbook,
  getEmployeeCountries,
  addNewHandbook,
  getEmployeeHandbook,
  updateEmployeeHandbook,
  getAllPublishedPolicies,
  getPolicyVersion,
  addHandBookDiscription,
  getMessageDescription,
  getHandbookPolicyReport,
  exportHandbookPolicyReport,
}

const employeeHandbookSettingSelectors = {
  isLoading,
  employeeHandbooks,
  totalHandbookList,
  listSize,
  employeeCountries,
  updateHandbookPage,
  selectedCountries,
  getAllPoliciesDashboard,
  getDescription,
  getEditHandbook,
  getIsLoadingGet,
  getVersionLength,
  getEditPolicyMessage,
  getHandbookPolicyList,
}

export const employeeHandbookSettingService = {
  ...employeeHandbookSettingsThunk,
  actions: employeeHandbookSettingSlice.actions,
  selectors: employeeHandbookSettingSelectors,
}

export default employeeHandbookSettingSlice.reducer
